import EngineShell, { type SubTabDef } from '../components/EngineShell';
import DataIntervention, {
  ActionIntervention, MetricTiles, MiniTable, StatusPill,
} from '../components/engine/DataIntervention';
import { api } from '../api/client';
import { useState } from 'react';
import Engine4_Brand from './Engine4_Brand';

/**
 * Engine 5 · Go-to-Market (CPG value chain · stage 5).
 * Marketing, sales & service. Fandom-to-SKU is the lighthouse. The near-zero
 * paid-ad-spend guardrail is the moat.
 */

interface AdSpendResp { paid_spend_pct_revenue: number; alarm: boolean }

interface MarkupAttempt { retailer: string; market: string; attempted_price: number; status: string }
interface TradeControlResp { prepriced_coverage: number; retailer_markup_attempts: MarkupAttempt[] }

interface CollabHeat { name: string; heat_index: number; status: string }
interface EarnedMediaResp { collab_heat: CollabHeat[]; organic_reach: number; cultural_index: number }

interface Cohort { cohort: string; members: number; retention: number; ltv_usd: number }
interface ClubZonaResp { cohorts: Cohort[]; cac_ltv: number; retention: number; churn_drivers: string[] }

interface FlaggedPost { post_id: string; reason: string; severity: string }
interface CommunityResp { flagged_posts: FlaggedPost[]; sentiment_index: number; auto_moderated: boolean }

interface ServiceResp { resolution: string; escalate: boolean }
interface GuaranteeResp { disposition: string; freshness_action: string }

const pct = (v: number) => `${v.toFixed(1)}%`;
const compact = (v: number) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v));

export default function Engine5Shell() {
  const subTabs: SubTabDef[] = [
    {
      id: 'fandom_to_sku', name: 'Fandom-to-SKU', tier: 'lighthouse', moat: 'core', enabled: true,
      render: () => <Engine4_Brand />,
    },
    {
      id: 'ad_spend_guardrail', name: 'Ad-Spend Guardrail', tier: 'flagship', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<AdSpendResp>
          objective="O5 · hold paid ad spend near zero — earned media is the moat"
          workflow={['Pull paid spend as % of revenue', 'Compare to the near-zero guardrail', 'Alarm on breach', 'Redirect to earned media']}
          decisionRules={{ rules: ['Alarm if paid spend > guardrail', 'Default to organic + collab reach'] }}
          aiLogic={{ description: 'Deterministic spend-ratio guardrail check.', modality: 'deterministic', hitlLevel: 'L3 approval gating', moat: 'core' }}
          kpi={{ primary: 'Paid spend % of revenue', owner: 'CMO / Finance' }}
          queryKey={['gtm', 'ad-spend']} fetcher={() => api.get<AdSpendResp>('/api/gtm/ad-spend-ratio')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Paid spend / revenue', value: pct(d.paid_spend_pct_revenue), color: d.alarm ? 'red' : 'olive' },
                { label: 'Guardrail', value: d.alarm ? 'Breached' : 'Holding', color: d.alarm ? 'red' : 'olive' },
              ]} />
              <div className="small faint">Moat guardrail: near-zero paid ad spend. Growth runs on earned media and fandom, not bought reach.</div>
            </>
          )}
        />
      ),
    },
    {
      id: 'trade_control', name: 'Trade Control', tier: 'flagship', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<TradeControlResp>
          objective="O5 · hold the 99c shelf price — block retailer markup"
          workflow={['Pull pre-priced coverage', 'Detect markup attempts', 'Flag + resolve violations', 'Protect the price promise']}
          decisionRules={{ rules: ['Flag any price above the 99c pre-print', 'Resolve markup attempts before they spread'] }}
          aiLogic={{ description: 'Deterministic pre-priced coverage + markup detection.', modality: 'deterministic', hitlLevel: 'L3 approval gating', moat: 'core' }}
          kpi={{ primary: 'Pre-priced coverage + markup attempts', owner: 'VP Sales / Trade' }}
          queryKey={['gtm', 'trade-control']} fetcher={() => api.get<TradeControlResp>('/api/gtm/trade-control')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Pre-priced coverage', value: pct(d.prepriced_coverage * 100), color: d.prepriced_coverage >= 0.9 ? 'olive' : 'gold' },
                { label: 'Markup attempts', value: String(d.retailer_markup_attempts.length), color: d.retailer_markup_attempts.length ? 'gold' : 'olive' },
              ]} />
              <MiniTable title="Markup attempts" rows={d.retailer_markup_attempts} rowKey={(r, i) => `${r.retailer}-${i}`}
                columns={[
                  { header: 'Retailer · market', cell: (r) => <><b>{r.retailer}</b> <span className="small faint">{r.market}</span></> },
                  { header: 'Attempted', align: 'right', cell: (r) => <span className="mono" style={{ color: 'var(--red)' }}>${r.attempted_price.toFixed(2)}</span> },
                  { header: 'Status', align: 'right', cell: (r) => <StatusPill status={r.status} /> },
                ]} />
            </>
          )}
        />
      ),
    },
    {
      id: 'earned_media', name: 'Earned Media', tier: 'flagship', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<EarnedMediaResp>
          objective="O5 · run the earned-media flywheel — organic reach, hot collabs"
          workflow={['Track collab heat', 'Measure organic reach + cultural index', 'Double down on hot collabs', 'Feed wins to Fandom-to-SKU']}
          decisionRules={{ rules: ['Scale hot collabs', 'Watch cooling collabs before they fade'] }}
          aiLogic={{ description: 'Deterministic collab-heat + reach scoring from social signals.', modality: 'ml', hitlLevel: 'L2 exception review', moat: 'core' }}
          kpi={{ primary: 'Organic reach + cultural index + collab heat', owner: 'CMO / Social' }}
          queryKey={['gtm', 'earned-media']} fetcher={() => api.get<EarnedMediaResp>('/api/gtm/earned-media')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Organic reach', value: compact(d.organic_reach), color: 'olive' },
                { label: 'Cultural index', value: d.cultural_index.toFixed(2), color: 'ink' },
              ]} />
              <MiniTable title="Collab heat" rows={d.collab_heat} rowKey={(r, i) => `${r.name}-${i}`}
                columns={[
                  { header: 'Collab', cell: (r) => <b>{r.name}</b> },
                  { header: 'Heat', align: 'right', cell: (r) => <span className="mono">{(r.heat_index * 100).toFixed(0)}</span> },
                  { header: 'Status', align: 'right', cell: (r) => <StatusPill status={r.status} /> },
                ]} />
            </>
          )}
        />
      ),
    },
    {
      id: 'clubzona_cac_ltv', name: "Club 'Zona CAC/LTV", tier: 'core', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<ClubZonaResp>
          objective="O5 · grow member LTV against CAC, cut churn"
          workflow={['Pull cohort retention + LTV', 'Watch CAC:LTV ratio', 'Target churn drivers', 'Bundle retention offers']}
          decisionRules={{ rules: ['Protect CAC:LTV ≥ 3x', 'Act on the top churn driver'] }}
          aiLogic={{ description: 'Deterministic cohort economics from membership data.', modality: 'ml', hitlLevel: 'L2 exception review', moat: 'core' }}
          kpi={{ primary: 'CAC:LTV + retention + cohort LTV', owner: 'CMO / DTC' }}
          queryKey={['gtm', 'clubzona']} fetcher={() => api.get<ClubZonaResp>('/api/gtm/clubzona')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'CAC : LTV', value: `${d.cac_ltv.toFixed(1)}x`, color: d.cac_ltv >= 3 ? 'olive' : 'gold' },
                { label: 'Retention', value: pct(d.retention * 100), color: 'ink' },
              ]} />
              <MiniTable title="Cohorts" rows={d.cohorts} rowKey={(r, i) => `${r.cohort}-${i}`}
                columns={[
                  { header: 'Cohort', cell: (r) => <b className="small">{r.cohort}</b> },
                  { header: 'Members', align: 'right', cell: (r) => <span className="mono">{compact(r.members)}</span> },
                  { header: 'Retention', align: 'right', cell: (r) => <span className="mono">{pct(r.retention * 100)}</span> },
                  { header: 'LTV', align: 'right', cell: (r) => <span className="mono">${r.ltv_usd.toFixed(1)}</span> },
                ]} />
              <div className="small faint" style={{ marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>Churn drivers</div>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {d.churn_drivers.map((c) => <span key={c} className="tag">{c}</span>)}
              </div>
            </>
          )}
        />
      ),
    },
    {
      id: 'service_automation', name: 'Service Automation', tier: 'flagship', moat: 'commodity', enabled: true,
      render: () => <ServiceAutomation />,
    },
    {
      id: 'complaint_guarantee', name: 'Complaint Guarantee', tier: 'flagship', moat: 'commodity', enabled: true,
      render: () => <ComplaintGuarantee />,
    },
    {
      id: 'community_moderation', name: 'Community Moderation', tier: 'flagship', moat: 'commodity', enabled: true,
      render: () => (
        <DataIntervention<CommunityResp>
          objective="O5 · keep the community healthy (hygiene)"
          workflow={['Pull flagged posts', 'Track sentiment index', 'Auto-moderate routine flags', 'Escalate severe cases']}
          decisionRules={{ rules: ['Auto-moderate amber flags', 'Escalate red flags to trust & safety'] }}
          aiLogic={{ description: 'ML content moderation with deterministic escalation rules.', modality: 'ml', hitlLevel: 'L1 sampling', moat: 'commodity' }}
          kpi={{ primary: 'Sentiment index + flagged posts', owner: 'Community / Trust & Safety' }}
          queryKey={['gtm', 'community']} fetcher={() => api.get<CommunityResp>('/api/gtm/community')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Sentiment index', value: d.sentiment_index.toFixed(2), color: d.sentiment_index >= 0.7 ? 'olive' : 'gold' },
                { label: 'Auto-moderated', value: d.auto_moderated ? 'Yes' : 'No', color: d.auto_moderated ? 'olive' : 'gold' },
              ]} />
              <MiniTable title="Flagged posts" rows={d.flagged_posts} rowKey={(r) => r.post_id}
                columns={[
                  { header: 'Post', cell: (r) => <b className="mono small">{r.post_id}</b> },
                  { header: 'Reason', cell: (r) => <span className="small">{r.reason}</span> },
                  { header: 'Severity', align: 'right', cell: (r) => <StatusPill status={r.severity} /> },
                ]} />
            </>
          )}
        />
      ),
    },
  ];

  return (
    <EngineShell
      engineId={5}
      engineName="Engine 5 · Go-to-Market (Marketing, Sales & Service)"
      engineCode="E5"
      subTabs={subTabs}
      backgrounds={[{ id: 'brand-ip', name: 'Brand/IP Monitor', status: 'ok' }]}
    />
  );
}

/* Service Automation — POST with a ticket */
function ServiceAutomation() {
  const [ticket, setTicket] = useState('where is my order');
  return (
    <ActionIntervention<ServiceResp>
      objective="O5 · auto-resolve routine service tickets (hygiene)"
      workflow={['Enter a ticket', 'Classify intent', 'Draft a resolution', 'Escalate only when needed']}
      decisionRules={{ rules: ['Auto-resolve routine intents', 'Escalate anything ambiguous'] }}
      aiLogic={{ description: 'GenAI ticket triage with deterministic escalation gate.', modality: 'genai', hitlLevel: 'L1 sampling', moat: 'commodity' }}
      kpi={{ primary: 'Resolution + escalation flag', owner: 'Customer service' }}
      initialState={{ resolution: 'auto-resolve order-status intent', escalate: false }}
      input={
        <>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Ticket</div>
          <input className="c2-input" value={ticket} onChange={(e) => setTicket(e.target.value)} style={{ width: 280 }} />
        </>
      }
      runLabel="Resolve ticket"
      action={() => api.gtmServiceAutomate(ticket) as Promise<ServiceResp>}
      render={(d) => (
        <MetricTiles tiles={[
          { label: 'Resolution', value: d.resolution, color: 'ink' },
          { label: 'Escalate', value: d.escalate ? 'Yes' : 'No', color: d.escalate ? 'gold' : 'olive' },
        ]} />
      )}
    />
  );
}

/* Complaint Guarantee — POST with a complaint */
function ComplaintGuarantee() {
  const [complaint, setComplaint] = useState('flat can');
  return (
    <ActionIntervention<GuaranteeResp>
      objective="O5 · honor the freshness guarantee on product complaints (hygiene)"
      workflow={['Enter a complaint', 'Determine disposition', 'Trigger freshness action', 'Capture lot for traceback']}
      decisionRules={{ rules: ['Issue freshness credit on valid complaints', 'Capture lot code for every claim'] }}
      aiLogic={{ description: 'Deterministic complaint disposition with freshness-action mapping.', modality: 'deterministic', hitlLevel: 'L1 sampling', moat: 'commodity' }}
      kpi={{ primary: 'Disposition + freshness action', owner: 'Customer service / QA' }}
      initialState={{ disposition: 'freshness credit approved', freshness_action: 'capture lot code and replace case' }}
      input={
        <>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Complaint</div>
          <input className="c2-input" value={complaint} onChange={(e) => setComplaint(e.target.value)} style={{ width: 280 }} />
        </>
      }
      runLabel="Resolve complaint"
      action={() => api.gtmServiceGuarantee(complaint) as Promise<GuaranteeResp>}
      render={(d) => (
        <MetricTiles tiles={[
          { label: 'Disposition', value: d.disposition, color: 'ink' },
          { label: 'Freshness action', value: d.freshness_action, color: 'ink' },
        ]} />
      )}
    />
  );
}
